/**
 * ObjectivesSearchResults
 *
 * Renders a scrollable list of objective search results as cards with a progress ring.
 * Each card shows the objective title, counts for linked Key Results and Initiatives,
 * and a circular progress indicator color-coded by objective status.
 *
 * Interactions
 * - Clicking a card navigates to the Company Dashboard with query params:
 *   - dashboardTab=Objectives
 *   - objectiveId=<id>
 *
 * Props
 * - results: Array of objective items to render.
 *
 * Edge cases
 * - When results is empty, shows a simple "No objectives found." placeholder.
 */
import React from "react";
import { Box, CircularProgress, Divider, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import "./objective-search-results.scss";
import { getProgressColor } from "../../../../features/company-dashboard/company-dashboard-utils";
import { routes } from "../../../../constants/routes";

interface Objective {
  objectiveId: number;
  objectiveName: string;
  keyResultsCount: number;
  initiativesCount: number;
  color: string;
  progress: number | undefined;
  description: string;
  status: string;
}

interface Props {
  results: Objective[];
}

const ObjectivesSearchResults: React.FC<Props> = ({ results }) => {
  const navigate = useNavigate();

  if (results.length === 0) {
    return <div className="no-results">No objectives found.</div>;
  }

  return (
    <Box className="objectives-search-results scrollable">
      {results.map((objective) => {
        const { mainColor, lightColor } = getProgressColor(objective.status);
        return (
          <Box
            key={objective.objectiveId}
            className="objective-card"
            sx={{ cursor: "pointer" }}
            onClick={() => {
              const queryParams = new URLSearchParams();
              queryParams.set("dashboardTab", "Objectives");
              queryParams.set("objectiveId", String(objective.objectiveId));
              navigate(`${routes.COMPANYDASHBOARD}?${queryParams.toString()}`);
            }}
          >
            {/* Left Section */}
            <Box className="objective-left">
              <Typography variant="subtitle1">
                {objective.objectiveName}
              </Typography>
              <Box className="objective-meta">
                <span>
                  Key Results <strong>{objective.keyResultsCount}</strong>
                </span>
                <Divider
                  orientation="vertical"
                  flexItem
                  style={{ marginRight: "8px", marginTop: "8px" }}
                />
                <span>
                  Initiatives <strong>{objective.initiativesCount}</strong>
                </span>
              </Box>
            </Box>

            {/* Right Section */}
            <Box className="progress-wrapper">
              {/* Background track (lighter shade of main color) */}
              <CircularProgress
                variant="determinate"
                value={100}
                size={60}
                thickness={5}
                sx={{ color: lightColor, position: "absolute" }}
              />
              {/* Foreground progress */}
              <CircularProgress
                variant="determinate"
                value={
                  typeof objective.progress === "string"
                    ? Number.parseFloat(objective.progress)
                    : objective.progress
                }
                size={60}
                thickness={5}
                sx={{
                  color: mainColor,
                  "& .MuiCircularProgress-circle": {
                    strokeLinecap: "round",
                  },
                }}
              />
              <Box className="progress-value">
                {Math.round(objective.progress || 0)}%
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default ObjectivesSearchResults;
